import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  InvestmentFinancialDepositsModel,
  InvestmentFinancialModel
} from './investment-financial.model';
import { ResultModel } from '../../../shared/result.model';
import { ApiResponse } from '../../../models/base';
import { AccountingAccountsModel } from '../../accounting/accounting-accounts/accounting-accounts.model';
import { unwrapV2, V2ApiResponse } from '../../../modules_v2/shared/utils/v2-api-response.util';

const INVESTMENT_ENDPOINT = '/investmentfinancial';
const DEPOSIT_ENDPOINT = '/investmentfinancialdeposit';
const V2_ENDPOINT = '/api/v2/financial-investments';

@Injectable({
  providedIn: 'root',
})
export class InvestmentFinancialService {
  constructor(private http: HttpClient) {}

  public downloadFileImage(uid: string): Promise<Blob> {
    let params = new HttpParams();
    params = params.append('investmentfinancialId', uid);

    return firstValueFrom(
      this.http.get(environment.apiUrl + '/investmentfinancial/file/downloadimage', {
        responseType: 'blob',
        params: params,
      })
    ).then((response) => {
      return new Blob([response], { type: 'image/png' });
    });
  }

  public downloadFilePDF(uid: string): Promise<Blob> {
    let params = new HttpParams();
    params = params.append('investmentfinancialId', uid);

    return firstValueFrom(
      this.http.get(environment.apiUrl + '/investmentfinancial/file/downloadpdf', {
        responseType: 'blob',
        params: params,
      })
    ).then((response) => {
      return new Blob([response], { type: 'application/pdf' });
    });
  }

  public get(uid: string): Promise<InvestmentFinancialModel> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<InvestmentFinancialModel>>(environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(uid))),
      'No se ha encontrado la inversión financiera.'
    );
  }

  public list(offset?: number, limit?: number): Promise<ResultModel<InvestmentFinancialModel>> {
    let params = new HttpParams();
    if (offset !== undefined && limit !== undefined) {
      params = params.set('offset', offset.toString()).set('limit', limit.toString());
    }

    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<ResultModel<InvestmentFinancialModel>>>(environment.apiUrl + V2_ENDPOINT, { params })),
      'No se pudieron obtener las inversiones financieras.'
    ).then((result) => {
      const list = new ResultModel<InvestmentFinancialModel>();
      list.list = result && Array.isArray(result.list) ? result.list : [];
      list.totalItems = result ? result.totalItems : list.list.length;
      return list;
    });
  }

  public accounts(): Promise<AccountingAccountsModel[]> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<AccountingAccountsModel[]>>(environment.apiUrl + V2_ENDPOINT + '/accounts')),
      'No se pudieron obtener las cuentas de inversiones financieras.'
    ).then((list) => (Array.isArray(list) ? list : []));
  }

  public add(investmentfinancial: InvestmentFinancialModel): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + INVESTMENT_ENDPOINT, investmentfinancial)
    ).then((response) => response.data);
  }

  public edit(investmentfinancial: InvestmentFinancialModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + INVESTMENT_ENDPOINT, investmentfinancial)
    ).then((response) => response.data);
  }

  public delete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('investmentfinancialId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + INVESTMENT_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public depositList(
    investmentfinancialUid: string
  ): Promise<InvestmentFinancialDepositsModel[]> {
    return unwrapV2(
      firstValueFrom(
        this.http.get<V2ApiResponse<InvestmentFinancialDepositsModel[]>>(
          environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(investmentfinancialUid) + '/deposits'
        )
      ),
      'No se pudieron obtener los pagos de la inversión.'
    ).then((list) => (Array.isArray(list) ? list : []));
  }

  public depositCreate(deposit: InvestmentFinancialDepositsModel): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + DEPOSIT_ENDPOINT, deposit)
    ).then((response) => response.data);
  }

  public depositUpdate(deposit: InvestmentFinancialDepositsModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + DEPOSIT_ENDPOINT, deposit)
    ).then((response) => response.data);
  }

  public depositDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('investmentfinancialdepositId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + DEPOSIT_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }
}